/**
 * Shared Table Style Tokens
 *
 * Canonical styles for all data tables in the app.
 * Import from here to ensure visual consistency across views.
 */

import { css } from "@emotion/react"
import { theme, alpha } from "../theme/theme"

export const tableStyles = {
  /** Wrapper: fill remaining space, clip overflow for Virtuoso */
  wrap: css`
    flex: 1;
    overflow: hidden;
    min-height: 0;
  `,

  /** The <table> element */
  table: css`
    width: 100%;
    border-collapse: collapse;
    font-size: 0.8125rem;
  `,

  /** <thead> — sticky with opaque background so content doesn't bleed through */
  thead: css`
    background: ${theme.colors.muted};
    position: sticky;
    top: 0;
    z-index: 2;
  `,

  /** Standard header cell (<th>). The bottom divider is rendered as a positioned
   *  pseudo-element (not border-bottom or box-shadow) so it survives scroll under
   *  `position: sticky` + `border-collapse: collapse`, where border merging makes
   *  the line flicker out as rows pass under the header. */
  th: css`
    padding: 0.5rem 0.75rem;
    text-align: left;
    font-size: 0.6875rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: ${theme.colors.mutedForeground};
    background: ${theme.colors.muted};
    white-space: nowrap;
    position: relative;
    &::after {
      content: "";
      position: absolute;
      left: 0;
      right: 0;
      bottom: 0;
      height: 1px;
      background: ${theme.colors.border};
      pointer-events: none;
    }
  `,

  /** Standard body cell (<td>) */
  td: css`
    padding: 0.5rem 0.75rem;
    color: ${theme.colors.foreground};
    white-space: nowrap;
  `,

  /** Standard table row */
  tr: css`
    border-bottom: 1px solid ${theme.colors.border};
    transition: background-color 0.1s;
    &:hover { background: ${alpha(theme.colors.muted, 0.3)}; }
  `,

  // ── Modifier classes ──────────────────────────────────────────────────────

  /** Right-align a cell */
  right: css`text-align: right;`,

  /** Center-align a cell */
  center: css`text-align: center;`,

  /** Muted text color */
  muted: css`color: ${theme.colors.mutedForeground};`,

  /** Tabular (monospaced) numbers */
  numeric: css`font-variant-numeric: tabular-nums;`,

  /** Sticky left column (frozen) — opaque so scrolled content doesn't show through */
  stickyCol: css`
    position: sticky;
    left: 0;
    z-index: 1;
    background: ${theme.colors.background};
  `,

  /** Sticky left column inside <thead> — matches the thead background */
  stickyColHead: css`
    position: sticky;
    left: 0;
    z-index: 2;
    background: ${theme.colors.muted};
  `,

  /** Total/summary column header (foreground, bolder) */
  thTotal: css`
    color: ${theme.colors.foreground};
    font-weight: 700;
  `,

  /** Minimum width for date/value columns */
  colMin: css`min-width: 5rem;`,

  /** Fixed width for date/value columns. Unlike `colMin`, this PINS the width
   *  (min == max) so the column can't stretch to fill `width: 100%`. Required
   *  when two side-by-side tables must keep their period columns aligned while
   *  scrolling — auto-layout would otherwise distribute leftover space
   *  differently per table (each has different non-period column totals),
   *  drifting the date columns apart. 5rem comfortably fits the values shown. */
  colFixed: css`
    width: 5rem;
    min-width: 5rem;
    max-width: 5rem;
  `,

  /** Header-group row cells: replace border-bottom with box-shadow so it stays visible during scroll */
  thGroupRow: css`
    border-bottom: none;
    box-shadow: inset 0 -1px 0 ${theme.colors.border};
  `,

  /** Background for past/current period columns (header cells) */
  pastPeriodHead: css`background-color: ${theme.colors.pastPeriodBg};`,

  /** Background for past/current period columns (body cells) */
  pastPeriodCell: css`background-color: ${theme.colors.pastPeriodBg};`,
}
